/**
 * Shared unit creation: validation and API payload building.
 * Used by AddNewUnit form and CSVImportUnits so both send the same shape to the API.
 * All unit-level validation (form object and CSV row) lives here.
 */

export const VALID_STATUSES = ['available', 'booked', 'blocked', 'sold', 'hold'];

/** Area keys we read from CSV or form for the area object. */
const AREA_KEYS = ['carpet_area', 'builtup_area', 'super_builtup_area', 'plot_area', 'area'];

/**
 * Normalize status to a valid value (lowercase, underscores).
 * @param {string} status
 * @returns {string}
 */
export function normalizeStatus(status) {
  if (!status || typeof status !== 'string') return 'available';
  const s = status.trim().toLowerCase().replace(/\s+/g, '_');
  return VALID_STATUSES.includes(s) ? s : 'available';
}

/**
 * Derive a single area number from payload.area (number or object of key→number).
 * Used as Unit.total_area / API "area".
 */
function getPrimaryArea(area) {
  if (area == null) return null;
  if (typeof area === 'number' && !Number.isNaN(area)) return area;
  if (typeof area === 'object') {
    const vals = Object.values(area).filter((v) => v != null && !Number.isNaN(parseFloat(v)));
    const num = vals.find((v) => Number(v) > 0);
    return num != null ? parseFloat(num) : null;
  }
  const parsed = parseFloat(area);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Normalize area row to slug (supports { slug, value, unit } or legacy { key, label, value, unit }).
 */
function getRowSlug(row) {
  if (row.slug && typeof row.slug === 'string') return row.slug.replace(/[^a-z0-9_]/g, '') || null;
  const key = (row.key || '').toString().trim();
  if (key) return key.replace(/[^a-z0-9_]/g, '') || null;
  const label = (row.label || '').toString().trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  return label || null;
}

/**
 * Convert light form area rows to area object and primary number.
 * Accepts array of { slug, value, unit } or legacy { key, label, value, unit }.
 * @param {{ slug?: string, key?: string, label?: string, value: number|string, unit?: string }[]} areaRows
 * @returns {{ areaObj: Object, primaryArea: number|null }}
 */
function areaRowsToArea(areaRows) {
  if (!Array.isArray(areaRows) || areaRows.length === 0) return { areaObj: {}, primaryArea: null };
  const areaObj = {};
  let primaryArea = null;
  const seen = new Set();
  areaRows.forEach((row) => {
    const slug = getRowSlug(row);
    const num = typeof row.value === 'number' ? row.value : parseFloat(row.value);
    if (!slug || Number.isNaN(num) || num < 0 || seen.has(slug)) return;
    seen.add(slug);
    areaObj[slug] = num;
    if (primaryArea == null) primaryArea = num;
  });
  return { areaObj, primaryArea };
}

/**
 * Validate form-like unit payload (same rules as Add New Unit form).
 * @param {Object} payload - { unit_number?, status?, area?, base_price? (or sale_price/price for compat), ... }
 * @param {{ requireArea?: boolean }} options - if true, at least one area value required
 * @returns {{ valid: boolean, errors: Object }}
 */
export function validateUnitPayload(payload, options = {}) {
  const { requireArea = true } = options;
  const errors = {};
  const unitNumber = (payload?.unit_number ?? '').toString().trim();
  if (!unitNumber) errors.unit_number = 'Unit number is required';

  if (requireArea) {
    const primary = getPrimaryArea(payload?.area);
    if (primary == null || primary <= 0) {
      errors.area = 'At least one area value is required';
    }
  }

  const basePrice = payload?.base_price ?? payload?.sale_price ?? payload?.price;
  if (basePrice === '' || basePrice === undefined || basePrice === null) {
    errors.base_price = 'Base price is required';
  } else if (Number.isNaN(parseFloat(basePrice)) || parseFloat(basePrice) < 0) {
    errors.base_price = 'Enter a valid base price';
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate light Add Unit form payload (name, towerBlock, basePrice, areaRows, etc.).
 * @param {Object} payload - { name?, unit_code?, tower_block?, facing?, base_price?, area_rows?, commission_rate?, ... }
 * @param {{ requireArea?: boolean }} options
 * @returns {{ valid: boolean, errors: Object }}
 */
export function validateLightUnitPayload(payload, options = {}) {
  const { requireArea = true } = options;
  const errors = {};
  const name = (payload?.name ?? '').toString().trim();
  if (!name) errors.name = 'Unit name is required';

  // floor, tower_block, facing are optional (e.g. not applicable for farmland, plot, etc.)

  const basePrice = payload?.base_price ?? payload?.sale_price ?? payload?.price;
  if (basePrice === '' || basePrice === undefined || basePrice === null) {
    errors.base_price = 'Base price is required';
  } else if (Number.isNaN(parseFloat(basePrice)) || parseFloat(basePrice) < 0) {
    errors.base_price = 'Enter a valid base price';
  }

  if (requireArea) {
    const { primaryArea } = areaRowsToArea(payload?.area_rows ?? []);
    if (primaryArea == null || primaryArea <= 0) {
      errors.area = 'At least one area value is required';
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Build the payload expected by the unit create API (project/configuration set by view).
 * Only Unit model primary fields at top level; everything else goes in metadata.
 * Primary fields: name, unit_code, status, base_price (sent as price), area, commission_rate, key_highlights, tags, metadata.
 *
 * @param {Object} payload - Legacy: { unit_number, status?, floor?, tower_wing?, facing?, area?, base_price? (or sale_price?), tags? }
 *   Light form: { name, unit_code?, tower_block?, facing?, base_price?, per_sqft_price?, commission_rate?, area_unit?, area_rows?, key_highlights?, tags?, other_details? }
 * @param {{ areaUnit?: string }} options - default 'sqft'
 * @returns {Object} API payload: { name, unit_code, inventory_status, price, area, commission_rate?, key_highlights?, tags, metadata }
 */
const UNIT_PRIMARY_KEYS = new Set([
  'name', 'unit_code', 'status', 'inventory_status', 'base_price', 'sale_price', 'price', 'area', 'area_rows',
  'commission_rate', 'key_highlights', 'tags', 'project', 'configuration', 'metadata',
  'floor', 'unit_block', 'facing',
]);

function metadataWithoutPrimaryFields(obj) {
  if (!obj || typeof obj !== 'object') return {};
  return Object.fromEntries(
    Object.entries(obj).filter(([k]) => !UNIT_PRIMARY_KEYS.has(k))
  );
}

export function buildUnitApiPayload(payload, options = {}) {
  const areaUnit = options?.areaUnit ?? 'sqft';

  // Light form: has name and area_rows or other light-only fields
  const hasLightForm = payload && (payload.name !== undefined || Array.isArray(payload.area_rows));
  if (hasLightForm) {
    const name = (payload.name ?? '').toString().trim() || 'Unit';
    const unitCode = (payload.unit_code ?? '').toString().trim() || null;
    const { areaObj, primaryArea } = areaRowsToArea(payload.area_rows ?? []);
    const areaRowsPayload = Array.isArray(payload.area_rows)
      ? payload.area_rows
        .filter((r) => r && (r.slug || r.key) && (r.value != null && !Number.isNaN(parseFloat(r.value))))
        .map((r) => ({
          key: getRowSlug(r) || (r.slug || r.key || '').toString(),
          value: typeof r.value === 'number' ? r.value : parseFloat(r.value),
          unit: (r.unit || payload.area_unit || areaUnit || 'sqft').toString(),
        }))
      : [];

    // Code fields: floor, unit_block, facing at top level (not in metadata)
    const floorNum = payload.floor != null && payload.floor !== '' ? (Number(payload.floor) || null) : null;
    const unitBlock = (payload.tower_block ?? payload.tower_wing ?? '').toString().trim() || null;
    const facingVal = (payload.facing ?? '').toString().trim() || null;
    const basePriceVal = payload.base_price != null && payload.base_price !== '' ? parseFloat(payload.base_price) : null;
    const legacyPriceVal = payload.sale_price != null && payload.sale_price !== '' ? parseFloat(payload.sale_price) : null;
    const priceVal = basePriceVal ?? legacyPriceVal;
    const perSqftVal = payload.per_sqft_price != null && payload.per_sqft_price !== '' ? parseFloat(payload.per_sqft_price) : null;
    const otherDetailsFiltered = Array.isArray(payload.other_details) ? payload.other_details.filter((r) => (r?.label ?? r?.value ?? '').toString().trim()) : [];

    const metadata = {
      ...metadataWithoutPrimaryFields(payload.metadata),
      ...(perSqftVal != null && !Number.isNaN(perSqftVal) && { per_sqft_price: perSqftVal }),
      ...(otherDetailsFiltered.length > 0 && { other_details: otherDetailsFiltered }),
    };

    const result = {
      name,
      unit_code: unitCode,
      inventory_status: normalizeStatus(payload.status ?? 'available'),
      price: priceVal != null && !Number.isNaN(priceVal) ? priceVal : null,
      area: areaRowsPayload.length > 0 ? areaRowsPayload : primaryArea,
      floor: floorNum,
      unit_block: unitBlock,
      facing: facingVal,
      metadata,
      tags: Array.isArray(payload.tags) ? payload.tags.map((t) => String(t).trim()).filter(Boolean) : [],
    };
    if (payload.commission_rate != null && payload.commission_rate !== '') {
      result.commission_rate = parseFloat(payload.commission_rate);
    }
    if (Array.isArray(payload.key_highlights)) {
      result.key_highlights = payload.key_highlights.filter((h) => (h ?? '').toString().trim());
    }
    return result;
  }

  // Legacy form / CSV: floor, unit_block, facing at top level; rest in metadata
  const unitNumber = (payload?.unit_number ?? '').toString().trim();
  const name = unitNumber || 'Unit';
  const status = normalizeStatus(payload?.status);
  const areaVal = getPrimaryArea(payload?.area);
  const metadata = {
    ...metadataWithoutPrimaryFields(payload?.metadata),
    area_unit: payload?.area_unit ?? areaUnit,
    optional_fields: payload?.optional_fields ?? null,
  };
  const priceVal = payload?.base_price != null ? parseFloat(payload.base_price) : (payload?.sale_price != null ? parseFloat(payload.sale_price) : null);
  return {
    name,
    unit_code: unitNumber || null,
    inventory_status: status,
    price: priceVal != null && !Number.isNaN(priceVal) ? priceVal : null,
    area: areaVal,
    floor: payload?.floor != null && payload?.floor !== '' ? (Number(payload.floor) || null) : null,
    unit_block: (payload?.tower_wing ?? payload?.tower_block ?? '').toString().trim() || null,
    facing: (payload?.facing ?? '').toString().trim() || null,
    metadata,
    tags: Array.isArray(payload?.tags) ? payload.tags : [],
  };
}

/**
 * Build form-like unit payload from a CSV row (same shape as AddNewUnit form).
 * Use this so CSV and form share one validation + buildUnitApiPayload path.
 * @param {Object} row - Raw row object (e.g. from rowsToObjects, keys = column names)
 * @param {Function} getRowValue - (row, ...keys) => string; returns first matching value (e.g. from importCSVUtils.getRowValue)
 * @returns {Object} Form-like payload: { unit_number, status, floor, tower_wing, facing, area?, base_price?, tags }
 */
export function rowToFormPayload(row, getRowValue) {
  const area = {};
  AREA_KEYS.forEach((key) => {
    const v = getRowValue(row, key);
    if (v && !Number.isNaN(parseFloat(v))) area[key] = parseFloat(v);
  });
  const facing = getRowValue(row, 'facing');
  const basePriceStr = getRowValue(row, 'base_price', 'base price') || getRowValue(row, 'sale_price', 'sale price');
  return {
    unit_number: getRowValue(row, 'unit_number', 'unit number').trim(),
    status: getRowValue(row, 'status') || 'available',
    floor: getRowValue(row, 'floor') || null,
    tower_wing: getRowValue(row, 'tower_wing', 'tower_wing') || null,
    facing: facing && facing !== '—' ? facing : null,
    base_price: basePriceStr ? parseFloat(basePriceStr) : null,
    area: Object.keys(area).length > 0 ? area : null,
    tags: (getRowValue(row, 'tags') || '').split(',').map((t) => t.trim()).filter(Boolean),
  };
}

/**
 * Validate one CSV row with same unit-level rules as the form.
 * @param {Object} row - Raw CSV row object
 * @param {number} rowIndex - 0-based row index (for error messages)
 * @param {{ getRowValue: Function, requireArea?: boolean }} options - getRowValue(row, ...keys) => string; requireArea default false for CSV
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateUnitRow(row, rowIndex, options = {}) {
  const { getRowValue, requireArea = false } = options;
  const formPayload = rowToFormPayload(row, getRowValue);
  const { valid, errors } = validateUnitPayload(formPayload, { requireArea });
  const messages = [];
  if (errors.unit_number) messages.push(`Row ${rowIndex + 1}: ${errors.unit_number}`);
  if (errors.area) messages.push(`Row ${rowIndex + 1}: ${errors.area}`);
  if (errors.base_price) messages.push(`Row ${rowIndex + 1}: ${errors.base_price}`);
  return { valid, errors: messages };
}
